import * as React from "react";
import { Dialog as HeadlessDialog } from "@headlessui/react";
import { IconX } from "@tabler/icons-react";

import { Dialog } from "@/components/ui/dialog";
import { ratioToNumber, type MetaAspectRatio } from "@/config/contentProgramUpload";
import { cn } from "@/lib/utils";

/**
 * One creative at full size. The panel is a size container and the frame
 * is `min(100cqw, 100cqh * ratio)` wide with the file's own aspect ratio,
 * so the media fills whichever axis runs out first, never cropped, never
 * letterboxed. The ratio comes from the loaded file itself; the placement
 * label only seeds the frame until the file reports its size.
 */

/** Width over height once the media has loaded, else the seeded ratio. */
export function useMediaRatio(mediaUrl: string | null | undefined, seed: MetaAspectRatio | null | undefined) {
  const [natural, setNatural] = React.useState<number | null>(null);
  React.useEffect(() => setNatural(null), [mediaUrl]);
  const ratio = natural ?? (seed ? ratioToNumber(seed) : 16 / 9);
  return { ratio, setNatural };
}

export function MediaFit({
  mediaUrl,
  mediaType,
  ratio,
  onRatio,
  autoPlay = false,
  className,
  placeholder,
}: {
  mediaUrl: string | null | undefined;
  mediaType: "IMAGE" | "VIDEO" | null | undefined;
  ratio: number;
  onRatio?: (ratio: number) => void;
  autoPlay?: boolean;
  className?: string;
  placeholder?: React.ReactNode;
}) {
  return (
    <div
      className={cn("relative w-full overflow-hidden rounded-md bg-background ring-1 ring-border", className)}
      style={{ aspectRatio: String(ratio), width: `min(100cqw, calc(100cqh * ${ratio}))` }}
    >
      {mediaUrl && mediaType === "VIDEO" ? (
        <video
          src={mediaUrl}
          controls
          autoPlay={autoPlay}
          muted={autoPlay}
          playsInline
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            if (v.videoWidth && v.videoHeight) onRatio?.(v.videoWidth / v.videoHeight);
          }}
          className="absolute inset-0 h-full w-full object-contain"
        />
      ) : mediaUrl ? (
        <img
          src={mediaUrl}
          alt=""
          onLoad={(e) => {
            const i = e.currentTarget;
            if (i.naturalWidth && i.naturalHeight) onRatio?.(i.naturalWidth / i.naturalHeight);
          }}
          className="absolute inset-0 h-full w-full object-contain"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
          {placeholder ?? "No preview"}
        </div>
      )}
    </div>
  );
}

/** The muted panel the frame is centred in; give it a height. */
export function MediaPanel({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={cn("flex items-center justify-center bg-muted p-4 [container-type:size] lg:p-6", className)}>
      {children}
    </div>
  );
}

/**
 * The submission dialog shell: media on one side, an identity header, a
 * scrolling body of facts and one footer of actions on the other. Wide
 * media takes the full width with the facts under it; tall media sits
 * beside them.
 */
export function SubmissionDialog({
  open,
  onOpenChange,
  srTitle,
  mediaUrl,
  mediaType,
  ratioLabel,
  header,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  srTitle: string;
  mediaUrl: string | null | undefined;
  mediaType: "IMAGE" | "VIDEO" | null | undefined;
  ratioLabel?: MetaAspectRatio | null;
  header: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const { ratio, setNatural } = useMediaRatio(open ? mediaUrl : null, ratioLabel);
  const landscape = ratio > 1.05;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} panelClassName="max-w-4xl">
      <div className={cn("flex max-h-[90vh] flex-col", !landscape && "lg:h-[min(90vh,44rem)] lg:flex-row")}>
        <HeadlessDialog.Title className="sr-only">{srTitle}</HeadlessDialog.Title>

        <MediaPanel className={cn("shrink-0", landscape ? "h-[42vh] w-full" : "h-[38vh] lg:h-full lg:w-[46%]")}>
          <MediaFit mediaUrl={mediaUrl} mediaType={mediaType} ratio={ratio} onRatio={setNatural} autoPlay />
        </MediaPanel>

        <div
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col border-t border-border",
            !landscape && "lg:border-l lg:border-t-0"
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div className="flex min-w-0 items-center gap-3">{header}</div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="-mr-1.5 -mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <IconX size={16} stroke={1.75} />
            </button>
          </header>
          <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-3", landscape && "lg:max-h-[38vh]")}>
            {children}
          </div>
          {footer && (
            <footer className="flex items-center justify-end gap-4 border-t border-border px-5 py-3">{footer}</footer>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/** Just the media, for a sample or a quick look. */
export function MediaLightbox({
  open,
  onOpenChange,
  mediaUrl,
  mediaType,
  ratioLabel,
  title = "Preview",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mediaUrl: string | null | undefined;
  mediaType: "IMAGE" | "VIDEO" | null | undefined;
  ratioLabel?: MetaAspectRatio | null;
  title?: string;
}) {
  const { ratio, setNatural } = useMediaRatio(open ? mediaUrl : null, ratioLabel);
  return (
    <Dialog open={open} onOpenChange={onOpenChange} panelClassName="max-w-4xl">
      <HeadlessDialog.Title className="sr-only">{title}</HeadlessDialog.Title>
      <div className="relative">
        <MediaPanel className="h-[80vh]">
          <MediaFit mediaUrl={mediaUrl} mediaType={mediaType} ratio={ratio} onRatio={setNatural} autoPlay />
        </MediaPanel>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          className="absolute right-3 top-3 inline-flex size-7 items-center justify-center rounded-md bg-card text-muted-foreground ring-1 ring-border transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <IconX size={16} stroke={1.75} />
        </button>
      </div>
    </Dialog>
  );
}
